import posthog from 'posthog-js';
import { trackApiError, redactApiBodyString, redactPosthogProperties } from './analytics';

/**
 * Global fetch interceptor for automatic PostHog API logging
 * This intercepts all fetch calls without modifying existing code
 */

let isInterceptorActive = false;

/**
 * Extract URL name from API URL (full path after /api, removing trailing numeric IDs)
 */
const extractUrlName = (url: string): string => {
  try {
    // Extract everything after /api/ until query string, hash, or end of string
    const apiMatch = url.match(/\/api\/([^?#]+)/);
    if (apiMatch && apiMatch[1]) {
      let path = apiMatch[1];
      
      // Split path into segments
      const segments = path.split('/');
      
      // Remove the last segment if it's purely numeric (an ID like agentId)
      if (segments.length > 0 && /^\d+$/.test(segments[segments.length - 1])) {
        segments.pop();
      }
      
      // Return the cleaned path with /api/ prefix
      return `api/${segments.join('/')}`;
    }
    
    return 'unknown';
  } catch (error) {
    console.warn('Error extracting URL name:', error);
    return 'unknown';
  }
};

export const initializeFetchInterceptor = () => {
  if (isInterceptorActive) {
    console.log('Fetch interceptor already active');
    return;
  }

  if (typeof window === 'undefined') {
    console.log('Fetch interceptor: Window not available');
    return;
  }

  // Store the original fetch function
  const originalFetch = window.fetch;

  // Override the global fetch function
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [url, options = {}] = args;
    const startTime = Date.now();
    const method = options.method || 'GET';
    
    // Extract URL string if it's a Request object
    const urlString = typeof url === 'string' ? url : url.toString();
    
    // Extract URL name (text after /api)
    const urlName = extractUrlName(urlString);
    
    // Extract request body for logging
    const requestBody = options.body ? (typeof options.body === 'string' ? options.body : '[Binary/FormData]') : null;
    
    // Skip logging for non-API calls (static assets, etc.)
    if (!urlString.includes('/api/') && !urlString.includes('localhost:8080')) {
      return originalFetch(...args);
    }

    try {
      const response = await originalFetch(...args);
      const duration = Date.now() - startTime;
      
      // Check if response is successful (2xx status codes)
      if (response.ok) {
        // Clone response to read body without consuming the original
        const responseClone = response.clone();
        
        // Extract response body for logging
        let responseBody = null;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const responseText = await responseClone.text();
            responseBody = responseText; 
          } else if (contentType && (contentType.includes('text/') || contentType.includes('application/'))) {
            const responseText = await responseClone.text();
            responseBody = responseText.substring(0, 1000); // Limit to 1000 chars for non-JSON
          }
        } catch (bodyError) {
          responseBody = '[Unable to read response body]';
        }
        
        // Log API call success with response body
        posthog.capture(
          `api_call_success`,
          redactPosthogProperties({
            'data-mg-api-method': method,
            'data-mg-api-url': urlString,
            'data-mg-api-status': response.status,
            'data-mg-api-request': redactApiBodyString(requestBody),
            'data-mg-api-response': redactApiBodyString(responseBody),
            'data-mg-api-duration-ms': duration,
            'data-mg-api-name': urlName,
          }),
        );
      } else {
        // Log API call failure for HTTP error responses (4xx, 5xx)
        
        trackApiError(`API call failed with status ${response.status}`, method, urlString, response.status,requestBody || undefined);
      }

      return response;
    } catch (error) {
      trackApiError(error instanceof Error ? error : new Error(String(error)), method, urlString, 0,requestBody || undefined);

      throw error;
    }
  };

  isInterceptorActive = true;
};

export const disableFetchInterceptor = () => {
  if (!isInterceptorActive) {
    return;
  }

  // Note: This is a simplified approach. In a real scenario, you'd need to store
  // the original fetch reference to restore it properly
  isInterceptorActive = false;
};

export const isFetchInterceptorActive = () => isInterceptorActive;

